import type { ToolDefinition, ToolType } from "../types";

const OPTIMIZE_IMAGE_CUSTOM_TOOL_TYPE = {
  label: "Precision Image Optimizer",
  icon: "Settings2",
} as const satisfies ToolType;

const optimizeImageCustomTool: ToolDefinition = {
  slug: "optimize-image-custom",
  name: "Custom Target KB Image Compressor",
  title: "Compress Image to Specific Size Online",
  description:
    "Reduce image file size to an exact target KB online for free. Compress JPEG or PNG to 100kb, 200kb, or any custom size with full manual control and no watermarks.",
  longDescription:
    "Take full control of your image optimization. The custom profile defaults to quality 85 JPEG output and original dimensions, while the UI lets you override target KB, quality, width, height, sharpen, and enhance only when you need precision. 100% free with no account required.",
  toolType: {
    label: "Custom Optimizer",
    icon: "Settings2",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "Custom Target KB",
    "No Watermarks",
    "Binary-Search Precision",
  ],
  category: "Optimization Tools",
  categorySlug: "image-optimization-tools",
  href: "/tools/optimize-image-custom",
  api: "/api/v1/optimize/custom",
  keywords: [
    "snappy photo compressor app",
    "passport photo 100kb maker",
    "picture file size checker",
    "how to decrease image kb",
    "high quality image optimizer software",
    "resize jpeg below 50kb",
    "jpeg upload size inspector",
    "decrease photo without losing quality",
    "custom kb size reducer online",
    "change picture to specific dimensions",
    "print resolution compression analyzer",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Reduce Image Size to a Specific KB",
        description:
          "Follow these simple steps to compress your image to an exact target size using our free online image compressor.",
      },
      benefits: {
        title: "Why Use Our Custom Image Compressor?",
        description:
          "Discover the advantages of having full manual control over your image optimization process.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Answers to common questions about reducing image file sizes to specific KB limits for web uploads and applications.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        title: "How to Compress Image to 100kb (Or Any Target Size) Online",
        level: "h2",
        paragraphs: [
          "Getting the right file size for your photos can be incredibly frustrating, especially when dealing with strict upload limits on government portals, job applications, or university admission forms. Most basic tools only offer generic 'low, medium, or high' presets, which often leaves you guessing and repeatedly downloading files just to check their size. Our custom online image size reducer changes that by giving you full manual control over the output.",
          "Whether you need to resize an image to 100kb for a visa application, compress a JPEG to 200kb for a website, or hit a precise 50kb limit for a passport photo, this tool eliminates the guesswork. By entering your exact target KB, our system uses an advanced binary-search compression algorithm. It continually tests different compression parameters in the background until it hits the closest possible match to your required file size.",
        ],
        note: "Our tool processes your images directly in your browser or through our secure server, returning the result in seconds without requiring registration.",
      },
      {
        kind: "text",
        title:
          "Decrease Image Size Without Losing Quality: How the Controls Work",
        level: "h2",
        paragraphs: [
          "To reduce picture size without losing quality unnecessarily, you need the right combination of settings. This tool provides primary inputs for Target KB, Quality, Width, Height, Sharpen, and Enhance. You can use these individually or combine them for the best results.",
          "The 'Target KB' field is perfect when you have a hard limit (e.g., 'upload must be under 100kb'). Leave Target KB, Quality, Width, and Height blank to use the custom profile defaults: no target KB, quality 85, JPEG output, and original dimensions. Add values only when you want to override those defaults. Sharpen and Enhance are optional toggles for extra visual refinement.",
        ],
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Common Use Cases for Custom KB Reduction",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Government & Visa Portals",
                icon: "Landmark",
                description:
                  "Easily shrink an image to 100kb for government portal upload tools and visa applications.",
                accent: "primary",
              },
              {
                label: "College Admission Forms",
                icon: "GraduationCap",
                description:
                  "Convert your photo to a 100kb JPEG to meet strict student portal requirements.",
                accent: "primary",
              },
              {
                label: "Web Performance",
                icon: "Zap",
                description:
                  "Compress high-resolution blog images to under 200kb to improve page loading speeds.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Typical File Size Limits for Online Uploads",
            description:
              "A quick reference guide for common image size requirements across different platforms.",
            columns: [
              "Application Type",
              "Typical Size Limit",
              "Recommended Format",
            ],
            rows: [
              {
                "Application Type": "Passport & Visa Photos",
                "Typical Size Limit": "50 KB – 100 KB",
                "Recommended Format": "JPEG / JPG",
              },
              {
                "Application Type": "College Application Forms",
                "Typical Size Limit": "100 KB – 200 KB",
                "Recommended Format": "JPEG / PNG",
              },
              {
                "Application Type": "Web & Blog Articles",
                "Typical Size Limit": "150 KB – 300 KB",
                "Recommended Format": "WebP / JPEG",
              },
              {
                "Application Type": "Forum Avatars & Profiles",
                "Typical Size Limit": "20 KB – 50 KB",
                "Recommended Format": "PNG / GIF",
              },
            ],
            note: "Always check the specific portal requirements, as some platforms may also enforce pixel dimension limits alongside KB limits.",
            emphasisColumns: ["Typical Size Limit"],
            monospaceColumns: [],
          },
        ],
      },
      {
        kind: "text",
        title: "The Best Free Online Image Compressor in 2026",
        level: "h2",
        paragraphs: [
          "Finding a reliable tool to decrease JPG size in KB shouldn't require downloading bulky software or paying for premium subscriptions. Our tool functions as the ultimate photo size reducer to 100kb, supporting high-resolution inputs and multiple formats including JPG, PNG, WebP, BMP, and TIFF.",
          "Unlike standard resizers that permanently degrade your image with heavy-handed presets, our custom kb size reducer online applies just enough compression to meet your goal. Whether you are using a desktop computer or trying to compress a passport size photo to 100kb online via Android or iOS, the responsive interface ensures a smooth, instant experience without any watermarks added to your final download.",
        ],
      },
    ],
    callouts: [
      {
        title: "Related Performance Tools",
        layout: "cards",
        iconColor: "accent",
        items: [
          {
            label: "SEO Image Optimizer",
            href: "/tools/optimize-seo-responsive-image",
            description:
              "Create responsive WebP variants when your image must serve different screen sizes.",
            accent: "accent",
          },
        ],
      },
    ],
    faqs: [
      {
        question:
          "How can I compress an image to exactly 100kb without losing quality?",
        answer:
          "To get as close to exactly 100kb as possible, simply upload your image and enter '100' into the Target KB field. Our tool uses binary-search precision to find the optimal balance of quality and compression to hit that target without making the image unnecessarily blurry.",
      },
      {
        question:
          "Why do portals reject my 100kb image and how do I fix the file size?",
        answer:
          "Portals often reject images because they strictly require the file to be *under* the limit, not exactly on it. If a portal has a 100kb limit, we recommend setting your Target KB to 95kb or 98kb to ensure the final file is safely beneath their maximum threshold.",
      },
      {
        question:
          "How do I reduce my photo size to 100kb for a passport application online?",
        answer:
          "Upload your photo, ensure the format is standard (like JPG), and enter '100' in the Target KB box. If the original image is a massive 4K file, it's also highly recommended to set smaller Width and Height values first, which helps preserve facial clarity during compression.",
      },
      {
        question: "Why is my 100kb compressed image grainy and how to fix it?",
        answer:
          "If your original image has massive pixel dimensions (like 4000x3000 pixels) and you force it into a tiny 100kb file, the compression algorithm has to aggressively degrade the image to hit the target, causing graininess. To fix this, set smaller Width and Height values along with your Target KB.",
      },
      {
        question:
          "How can I check if my compressed photo is under 100kb on a mobile device?",
        answer:
          "After clicking 'Download Optimized Image', you can check the file size on your phone by opening your Files app (or Gallery), long-pressing the downloaded image, and selecting 'Details' or 'Info'. The exact size in KB will be listed there.",
      },
      {
        question:
          "Can I use this image compressor online free without watermarks?",
        answer:
          "Yes. Our tool is 100% free to use, requires no account registration, and we never add watermarks or branding to your processed images.",
      },
      {
        question:
          "How can I increase an image's KB size to 100kb to meet portal requirements?",
        answer:
          "This specific tool is designed to compress and reduce file sizes. If you have an image that is 20kb and a portal requires a *minimum* of 100kb, you would typically need to upscale the image resolution or convert it to a less compressed format (like PNG or TIFF) using an image converter.",
      },
      {
        question:
          "Does compressing an image to 100kb remove its EXIF and location metadata?",
        answer:
          "During aggressive compression to hit strict KB targets, unnecessary data like EXIF metadata and location tags are often stripped out to save space. This actually improves your privacy when uploading documents to third-party portals.",
      },
    ],
    benefits: [
      {
        icon: "Target",
        title: "Exact Target Sizes",
        description:
          "Stop guessing with low/medium/high presets. Enter exactly how many kilobytes you need, and our binary-search algorithm will hit the target.",
      },
      {
        icon: "Sliders",
        title: "Full Manual Control",
        description:
          "Fine-tune your results by overriding target KB, quality percentage, width, height, sharpen, or enhance. Blank fields keep the default profile values.",
      },
      {
        icon: "ShieldCheck",
        title: "Private & Secure",
        description:
          "Your files are processed securely. We don't store your personal photos, making it safe for passport and visa documents.",
      },
      {
        icon: "Smartphone",
        title: "Works on Any Device",
        description:
          "Compress images directly from your desktop, Android, or iOS browser without needing to download a dedicated app.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload your image",
        description:
          "Drag and drop your image onto the upload zone or click to browse. A preview loads immediately. The tool supports JPG, PNG, WebP, BMP, TIFF, and GIF.",
      },
      {
        number: "02",
        title: "Use defaults or set overrides",
        description:
          "Leave fields blank to use the default custom profile: quality 85 JPEG with original dimensions. Enter a Target KB, adjust Quality, set Width and Height, or enable Sharpen and Enhance only when you need a custom result.",
      },
      {
        number: "03",
        title: "Click Optimize Custom",
        description:
          "Hit the button and watch the progress bar. The optimizer applies your exact parameters and returns the compressed result in seconds.",
      },
      {
        number: "04",
        title: "Download and verify",
        description:
          "Click 'Download Optimized Image' to save the file. Check the file size in your file manager to confirm it meets your upload requirement.",
      },
    ],
  },
};

export default optimizeImageCustomTool;
export { optimizeImageCustomTool };
